// JSON Schema handling shared by load-time validation and the build-time type generator.
//
// A reference is always two fields, `{ "table": "actions", "id": "jump" }` (suggestion "Versioned content registry:
// spine-plus-payload rows and two-field refs"; the table versions as a whole, so a ref names no version). A schema
// marks a reference site with the shorthand `{ "x-ref": "actions" }`; `expandRefs` turns it into the full object
// schema (table fixed to that name, a string id, nothing else), which both ajv and the type generator read, so the
// generated type is `{ table: "actions"; id: string }`.

/** A JSON Schema object (draft-07 / 2019-09 subset ajv reads). Kept loose: schemas are data the game writes. */
export type JsonSchema = { [keyword: string]: unknown };

/** The keyword that marks a reference site; its value is the target table's name. */
export const REF_KEYWORD = "x-ref" as const;

/** A two-field reference to a row of another (or the same) table. */
export interface ContentRef<T extends string = string> {
  table: T;
  id: string;
}

/** The pattern every row id and table name must match: stable, lower-case-friendly, dotted or dashed. */
export const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;

/** True for a plain JSON object. */
export function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Expand every `{ "x-ref": "<table>" }` shorthand in a schema into the full two-field reference schema, keeping the
 * keyword (load-time validation collects references through it). Returns a new schema; the input is not changed.
 */
export function expandRefs(schema: JsonSchema): JsonSchema {
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (!isObject(node)) return node;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) out[k] = k === "const" || k === "enum" || k === "default" || k === "examples" ? v : walk(v);
    const target = node[REF_KEYWORD];
    if (typeof target === "string") {
      return {
        ...out,
        type: "object",
        additionalProperties: false,
        required: ["table", "id"],
        properties: { table: { const: target }, id: { type: "string" } },
      };
    }
    return out;
  };
  return walk(schema) as JsonSchema;
}
